/**
 * Zod schemas for the auth surface — shared between API (validation) and
 * Web (form state + types). Inferred TS types are exported alongside each
 * schema for ergonomic consumption.
 *
 * Phase 2 (Wave 1, V2-FIX-04 / V2-FIX-08): consolidates invite-accept
 * (token-in-PATH variant), invite-create (with fullName), password-reset
 * issue / consume, and the new send-email request shapes that previously
 * lived inline in apps/api controllers. All schemas are `.strict()` so
 * unknown keys are rejected as VALIDATION_ERROR.
 *
 * Password policy comes from T4.3:
 *   - min length 8
 *   - at least one uppercase letter
 *   - at least one digit
 *   - at least one special character (printable ASCII non-alphanumeric)
 *
 * Phone format: E.164 (matches the user_phone_e164 CHECK in migration 002).
 */
import { z } from 'zod';
import { RoleSlug } from './enums';
import { ruEnum } from './lib/zod-ru';
import { RightEffect, UserRight } from './rights';

/**
 * E.164 phone shape: '+' followed by 10–15 digits, leading digit non-zero.
 * Mirrors the SQL CHECK so client-side validation does not surprise users
 * with a server-side rejection.
 */
export const E164_PHONE_REGEX = /^\+[1-9][0-9]{9,14}$/;

const PASSWORD_MIN_LENGTH = 8;

/**
 * Password validator: length + uppercase + digit + special.
 *
 * NOTE: we intentionally do not enforce Latin-only characters — Russian
 * speakers should be free to use Cyrillic in their passphrases. The
 * uppercase / digit / special checks are Unicode-aware where possible.
 */
export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, {
    message: `Пароль должен содержать минимум ${PASSWORD_MIN_LENGTH} символов`,
  })
  .refine((v) => /[A-ZА-ЯЁ]/.test(v), {
    message: 'Пароль должен содержать заглавную букву',
  })
  .refine((v) => /[0-9]/.test(v), {
    message: 'Пароль должен содержать цифру',
  })
  .refine((v) => /[^A-Za-zА-Яа-яЁё0-9]/.test(v), {
    message: 'Пароль должен содержать спецсимвол',
  });

/**
 * Email — RFC-style basic shape. Lower-cased on the API side via citext
 * column type; we trim on input to avoid accidental leading/trailing space.
 */
export const EmailSchema = z
  .string()
  .trim()
  .min(3, { message: 'Укажите email' })
  .max(254, { message: 'Email не должен превышать 254 символа' })
  .email({ message: 'Некорректный email' });

/** E.164 phone schema. */
export const PhoneSchema = z.string().trim().regex(E164_PHONE_REGEX, {
  message: 'Телефон должен быть в формате E.164 (например, +71234567890)',
});

/** Bare opaque token shape: non-empty, max 512 chars (refresh + invite + reset). */
const TokenSchema = z.string().min(1).max(512);

/** Full-name string — Cyrillic-friendly, 1..200 chars. Used in invite + user. */
export const FullNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Укажите ФИО' })
  .max(200, { message: 'ФИО не должно превышать 200 символов' });

// ─── Login ──────────────────────────────────────────────────────────────────

export const LoginRequestSchema = z
  .object({
    email: EmailSchema,
    // Русские сообщения о длине: дефолтный текст zod («String must contain
    // at least 1 character(s)») попадал на экран формы входа как есть.
    password: z
      .string()
      .min(1, { message: 'Введите пароль' })
      .max(256, { message: 'Пароль не должен превышать 256 символов' }),
  })
  .strict();
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

// ─── Invite create (Phase 2: fullName REQUIRED — closes G1/G4) ─────────────

/**
 * Body for `POST /invites`. Phase 2 makes `fullName` required so the
 * pending User row carries a real display name from the moment the invite
 * is issued (research-B G1 / G4 / V2-FIX-01).
 */
export const CreateInviteRequestSchema = z
  .object({
    email: EmailSchema,
    fullName: FullNameSchema,
    departmentId: z.number().int().positive().optional().nullable(),
    // Собственное русское сообщение БЕЗ перечисления значений: дефолтный
    // текст zod печатал весь список внутренних обозначений ролей (включая
    // те, что приглашающему не показываются) прямо в форму приглашения.
    roleSlug: z.nativeEnum(RoleSlug, ruEnum('Выберите роль')),
    /**
     * R6.4 — optional joining date (ISO `YYYY-MM-DD`). When omitted the
     * BE defaults to today (CURRENT_DATE per DB column default). Admin
     * may pre-set a past or future date so the workload calendar can
     * gate writes accordingly.
     */
    hireDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Дата должна быть в формате ГГГГ-ММ-ДД' })
      .optional(),
    /**
     * Feature 1 «Помощник ГИПа» (Wave 1). When inviting an ASSISTANT_GIP, the
     * GIP they assist (a user id). Optional/nullable; the binding is validated
     * by migration-034 triggers (target must be a GIP). Behaviour wiring lands
     * in later waves — schema-only here so `.strict()` does not silently strip
     * the field off the wire.
     */
    assistantOfUserId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateInviteRequest = z.infer<typeof CreateInviteRequestSchema>;

// ─── Invite acceptance (token in PATH — V2-FIX reconciled) ──────────────────
//
// The controller route is `POST /invites/:token/accept` — the body therefore
// does NOT carry the token itself. This closes the v1 drift where one
// schema had `token` in the body (auth.ts) and the controller used
// path-param. Path-variant is the live route (Phase 1 e2e relies on it).

export const AcceptInviteRequestSchema = z
  .object({
    /** New password — full policy enforced. */
    password: PasswordSchema,
    /** E.164 phone the new user attaches to their profile. */
    phone: PhoneSchema,
  })
  .strict();
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

// ─── Password reset (admin-issued; V2-FIX-08) ───────────────────────────────

/**
 * `POST /auth/password-reset/issue` — actor (Admin/РО) issues a reset for
 * `targetUserId`. RBAC matrix lives in the service.
 */
export const IssueResetRequestSchema = z
  .object({
    targetUserId: z.string().uuid(),
  })
  .strict();
export type IssueResetRequest = z.infer<typeof IssueResetRequestSchema>;

/**
 * `POST /auth/password-reset/:token` — public consumer. The token sits
 * in the URL (matches the live Phase 1 route); only the new password is
 * carried in the body.
 */
export const ConsumeResetRequestSchema = z
  .object({
    password: PasswordSchema,
  })
  .strict();
export type ConsumeResetRequest = z.infer<typeof ConsumeResetRequestSchema>;

/**
 * `POST /auth/password-reset/send-email` — Phase 2 NEW endpoint (W1.T9).
 *
 * The raw token is not stored on the server (only its HMAC hash). To re-
 * deliver the link by email after the original `issue` call, the caller
 * supplies the raw token in the body; the service hashes it, finds the
 * matching reset row, and dispatches the email. RBAC mirrors `/issue`.
 */
export const SendResetEmailRequestSchema = z
  .object({
    rawToken: TokenSchema,
  })
  .strict();
export type SendResetEmailRequest = z.infer<typeof SendResetEmailRequestSchema>;

/**
 * `POST /auth/password-reset/issue-emergency-admin/:targetAdminId` —
 * Phase-2 retroactive devil-advocate (Bar 8).
 *
 * The supervisor must re-authenticate with their current password EVERY
 * time they mint an emergency Admin reset link. The JWT session token
 * proves "this client is logged in"; this body field proves "the human
 * holding the keyboard is the supervisor and consents to the destructive
 * op right now". Combined, they prevent a hijacked session from quietly
 * minting reset links in the background.
 *
 * The password is NOT subject to `PasswordSchema` (which is the policy
 * for *new* passwords) — we just need a non-empty string to verify
 * against the stored argon2id hash. Length is bounded at 1024 to keep
 * the request body small (defense in depth — a 100KB payload would still
 * pass argon2.verify but is pointless).
 */
export const IssueEmergencyAdminResetRequestSchema = z
  .object({
    supervisorPassword: z.string().min(1).max(1024),
  })
  .strict();
export type IssueEmergencyAdminResetRequest = z.infer<typeof IssueEmergencyAdminResetRequestSchema>;

// ─── /auth/me response (Phase 3 — adds workloadViewLevel) ──────────────────

/**
 * Значение настройки `WORKLOAD_VIEW_LEVEL` (PLAN §9 / OQ-D §15.3): читается
 * из окружения на сервере и отдаётся в `GET /auth/me`.
 *
 * ⚠️ Ф8 · В-С (решение владельца 11.10.2026): ЗНАЧЕНИЙ ДВА. Третье,
 * `'employee'`, упразднено — оно отнимало у руководителей отделов и
 * администраторов положенное по должности (Р-229). Сервер с ним не
 * поднимается (`apps/api/src/config/env.schema.ts`).
 *
 * ⚠️ С Ф8 оставшиеся два значения не различаются ничем — ни на сервере, ни в
 * вебе. До Ф8 различались: при `'manager'` веб запирал администратору поле
 * «Отдел» и прятал «Все отделы» (данные бюро он видел и тогда); запор снят
 * волной В-З2. Настройка сохранена как имя в окружении; её судьбу решает
 * владелец. Прежний текст о поэтапном раскате (`employee` → `manager` →
 * `admin`, R10) описывал то, чего больше нет.
 */
export const WorkloadViewLevelSchema = z.enum(['manager', 'admin']);
export type WorkloadViewLevel = z.infer<typeof WorkloadViewLevelSchema>;

/**
 * `GET /auth/me` response shape — strict superset of the previous
 * payload (purely additive: pre-Phase-3 FE bundles ignore unknown keys).
 * Validation is `.passthrough()` so additive backend evolution does not
 * break clients that revalidate on the wire.
 *
 * The `user`-level fields mirror what `JwtStrategy.validate()` puts on
 * `request.user` (id / email / status / role / dept etc). They are
 * intentionally typed as a loose record at the schema layer because the
 * API surface is the source of truth — the FE consumes the response as
 * `MeResponse` and reads `workloadViewLevel` from it; full role-shape
 * validation belongs to the existing user/auth schemas elsewhere.
 */
/**
 * Блок 2 · Ф8 «Права доступа» · В-О2 — поправки к должности в ответах входа.
 *
 * ОДИН формат на три ответа: поле `rights` у `GET /auth/me` и `user.rights` у
 * `POST /auth/login` (обновление сессии пользователя в теле не отдаёт). Это
 * ровно `ActorRights` — вход ступени 1, а НЕ готовые ответы «можно / нельзя»:
 *
 *   own         — собственные действующие строки: право → `ALLOW` | `DENY`.
 *                 Сброшенные (`INHERIT`) и инертные по снимку должности сюда
 *                 не попадают — их отбрасывает сборка на сервере;
 *   deniedByGip — права, отнятые у ГИПа, чьим помощником человек служит
 *                 (Р-197); у остальных пусто.
 *
 * ⚠️ Читать — через `resolveRightState` + `matchRightState` (`rights.ts`,
 * договор чтения в шапке). Веб делает это одним хуком (`use-rights.ts`).
 *
 * ⚠️ Это для ИНТЕРФЕЙСА, не рубеж: сервер решает сам, на каждом запросе.
 */
export const ActorRightsSchema = z
  .object({
    own: z.record(z.nativeEnum(UserRight), z.enum([RightEffect.ALLOW, RightEffect.DENY])),
    deniedByGip: z.array(z.nativeEnum(UserRight)),
  })
  .strict();

export const MeResponseSchema = z
  .object({
    id: z.string().uuid(),
    email: z.string(),
    status: z.enum(['ACTIVE', 'PENDING', 'ARCHIVED']),
    mustChangePassword: z.boolean(),
    departmentId: z.number().int().nullable(),
    /** Phase 5 fix-pack FA-1 (closes F-01) — department name for FE chrome
     *  («Управление: <dept name>»). Null when `departmentId` is null. */
    departmentName: z.string().nullable(),
    /** Phase 3 W2.T3 — значение настройки `WORKLOAD_VIEW_LEVEL` (см. схему выше). */
    workloadViewLevel: WorkloadViewLevelSchema,
    /**
     * Feature 1 «Помощник ГИПа» (Wave 1). For an ASSISTANT_GIP, the id of the
     * GIP they assist; null for everyone else. `.optional()` in Wave 1 so the
     * contract is fixed WITHOUT requiring the API to emit it yet (no
     * toAuthUser/jwt.strategy change this wave — that lands later). The
     * surrounding schema is `.passthrough()`, so the API may begin emitting it
     * before this becomes required.
     */
    assistantOfUserId: z.string().uuid().nullable().optional(),
    /**
     * `Role.isAssignable` должности человека. Экран прав открыт тому же, кому
     * сервер открывает маршруты прав: уровень супервайзера И `false` здесь
     * (Блок 2 · Ф8 · В-Э, фикс-пак 1). Необязательное: образ API до волны поля
     * в ответе входа не отдаёт — веб читает отсутствие как «закрыто».
     */
    isAssignable: z.boolean().optional(),
    /** Блок 2 · Ф8 · В-О2 — поправки к должности; формат — `ActorRightsSchema`. */
    rights: ActorRightsSchema,
  })
  .passthrough();
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ─── Refresh token ──────────────────────────────────────────────────────────

/**
 * Refresh-token rotation. The token is normally read from an httpOnly cookie,
 * but we accept it in the body too so server-to-server callers can use the
 * same endpoint.
 */
export const RefreshRequestSchema = z
  .object({
    refreshToken: TokenSchema.optional(),
  })
  .strict();
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;
